import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, take, takeWhile, tap } from 'rxjs/operators';
import { LoginRequestStatus } from '../../../efaact-configuration/efaact-configuration.module';
import { Logo } from '../../../logo/store/logo.model';
import * as fromLogo from '../../../logo/store/logo.reducer';
import { UnsavedChangesService } from '../../../shared/modal-dialog';
import '../../../shared/modernizr';
import { LoggingService } from '../../../shared/services/logging/logging.service';
import { ISignInDialogData } from '../../ISignInDialogData';
import * as fromAuth from '../../store';
import { State } from '../../store/auth.reducer';

@Component({
    templateUrl: 'sign-in-dialog.component.html',
    styleUrls: ['./sign-in-dialog.component.scss'],
    standalone: false
})
export class SignInDialogComponent implements OnInit, OnDestroy {
    private componentActive = true;
    public signInData: ISignInDialogData;
    loginForm: UntypedFormGroup;
    LoginRequestStatus = LoginRequestStatus;
    logo$: Observable<Logo>;
    supported(): boolean {
        return (window as any).Modernizr.inputtypes.color;
    }
    constructor(
        public dialogRef: MatDialogRef<SignInDialogComponent>,
        public store: Store<State>,
        private log: LoggingService,
        private confirmService: UnsavedChangesService
    ) {
        // log.log({ type: 'signindialog.component:ctor', signInData });
    }
    ngOnDestroy(): void {
        this.componentActive = false;
    }

    ngOnInit() {
        this.log.debug({ type: 'SignInDialogComponent:ngOnInit' });

        this.loginForm = new UntypedFormGroup({
            username: new UntypedFormControl(null, Validators.required),
            password: new UntypedFormControl(null, Validators.required),
            pin: new UntypedFormControl(null, Validators.required),
            accessCode: new UntypedFormControl(null),
            securityAnswer: new UntypedFormControl(null),
            accessCodeDeliveryType: new UntypedFormControl(null),
            rememberMe: new UntypedFormControl(null),
            Resetpassword: new UntypedFormControl(false)
        });

        this.logo$ = this.store.pipe(
            select(fromLogo.selectAll),
            filter(x => Array.isArray(x) && x.length > 0),
            map(x => ({
                ...x[0],
                Uploadfile: 'data:image/png;base64,' + x[0].Uploadfile
            }))
        );

        this.store
            .pipe(
                select(fromAuth.getAuth),
                takeWhile(() => this.componentActive),
                tap(x =>
                    this.log.verbose({ type: 'SignInDialogComponent', x })
                ),
                map(auth => ({
                    ...auth,
                    accessCodeRequestMethods:
                        auth.accessCodeRequestMethods &&
                        auth.accessCodeRequestMethods.filter(
                            rm =>
                                !(
                                    rm.Value === 'email' &&
                                    !rm.DisplayName.includes('@')
                                )
                        )
                }))
            )
            .subscribe(auth => {
                this.log.verbose({
                    type: 'SignInDialogComponent:auth-subscribe',
                    auth,
                    signInData: this.signInData,
                    loginForm: this.loginForm.value
                });
                const controls = this.loginForm.controls;
                const accessCodeDeliveryType =
                    controls['accessCodeDeliveryType'].value &&
                    controls['accessCodeDeliveryType'].value.Value;
                this.signInData = {
                    ...this.signInData,
                    ...auth,
                    ...this.loginForm.value,
                    username: auth.username || controls['username'].value,
                    password: auth.password || controls['password'].value,
                    pin: auth.pin || controls['pin'].value,
                    rememberMe: auth.rememberMe || controls['rememberMe'].value,
                    accessCodeDeliveryType: accessCodeDeliveryType,
                    message: auth.message
                };
                this.log.verbose({
                    type: 'SignInDialogComponent:auth-subscribe2',
                    auth,
                    signInData: this.signInData,
                    loginForm: this.loginForm.value
                });
                controls['password'].setValidators([Validators.required]);
                controls['username'].setValidators([Validators.required]);
                controls['pin'].setValidators([Validators.required]);

                if (auth.status === LoginRequestStatus.AccessCodeSent) {
                    controls['password'].setValidators(null);
                    controls['username'].setValidators(null);
                    controls['pin'].setValidators(null);
                    controls['accessCode'].setValidators([Validators.required]);
                } else {
                    controls['accessCode'].setValidators(null);
                }

                if (auth.status === LoginRequestStatus.SelectAccessCodeMethod) {
                    if (
                        this.signInData.accessCodeRequestMethods &&
                        this.signInData.accessCodeRequestMethods.length === 1
                    ) {
                        controls['accessCodeDeliveryType'].setValue(
                            this.signInData.accessCodeRequestMethods[0]
                        );
                    }
                    controls['password'].setValidators(null);
                    controls['username'].setValidators(null);
                    controls['pin'].setValidators(null);
                    controls['accessCodeDeliveryType'].setValidators([
                        Validators.required
                    ]);
                } else {
                    controls['accessCodeDeliveryType'].setValidators(null);
                }

                this.patchForm(this.signInData);
                if (
                    auth.status === LoginRequestStatus.SelectAccessCodeMethod &&
                    !this.signInData.accessCodeDeliveryType &&
                    this.signInData.accessCodeRequestMethods &&
                    this.signInData.accessCodeRequestMethods.length === 1
                ) {
                    controls['accessCodeDeliveryType'].patchValue(
                        this.signInData.accessCodeRequestMethods[0]
                    );
                }
            });

        this.store
            .pipe(
                select(fromLogo.selectAll),
                takeWhile(() => this.componentActive)
            )
            .subscribe(logo => {
                this.log.verbose({ type: 'SignInDialogComponent', logo });
            });
    }

    rememberMeChanged(value: boolean) {
        this.log.debug({
            type: 'SignInDialogComponent:rememberMeChanged',
            value
        });
        const controls = this.loginForm.controls;
        if (!value) {
            this.store.dispatch(
                new fromAuth.RememberMe({
                    rememberMe: false,
                    username: null,
                    pin: null
                })
            );
        }
    }
    onNoClick(): void {
        this.dialogRef.close({ cancel: true });
    }

    close() {
        this.dialogRef.close({ cancel: true });
    }

    isAdminUsername(): boolean {
        const username = this.loginForm?.controls['username']?.value;
        return (
            typeof username === 'string' &&
            username.trim().toLowerCase() === 'admin'
        );
    }

    login() {
        sessionStorage.setItem('initialLoadForMainMsg', 'true');
        sessionStorage.setItem('IsSignout', 'false');
        const accessCode = this.loginForm.controls['accessCode']
            .value as string;
        this.log.verbose({
            type: 'SignInDialogComponent:login',
            loginForm: this.loginForm.value
        });
        if (this.loginForm.invalid) {
            return;
        }
        const controls = this.loginForm.controls;
        if (controls['rememberMe'].value) {
            this.store.dispatch(
                new fromAuth.RememberMe({
                    rememberMe: true,
                    username: controls['username'].value,
                    pin: controls['pin'].value
                })
            );
        } else {
            this.store.dispatch(
                new fromAuth.RememberMe({
                    rememberMe: false,
                    username: null,
                    pin: null
                })
            );
        }
        this.dialogRef.close({
            ...this.loginForm.value,
            accessCode: accessCode && accessCode.trimEnd()
        });
    }

    onForgotPassword() {
        this.log.verbose({ type: 'SignInDialogComponent:onForgotPassword' });
        this.confirmService
            .confirm({
                headerText: 'Confirm Password Reset',
                dialogText:
                    'This will reset your password and send the new password to the email address on file.'
            })
            .pipe(take(1))
            .subscribe(result => {
                if (result) {
                    this.loginForm.controls['Resetpassword'].setValue(true);
                    this.dialogRef.close(this.loginForm.value);
                }
            });
    }
    patchForm(data: ISignInDialogData) {
        this.log.verbose({ type: 'login.dialog:patchForm', data });
        const controls = this.loginForm.controls;
        this.loginForm.patchValue(data);
    }
}
